import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useLanguage } from "@/context/LanguageContext";
import { Header, Footer } from "@/components/Layout";
import { CheckCircle, Loader2, AlertCircle } from "lucide-react";
import axios from "axios";

const API_BASE_URL = "";
const API = `${API_BASE_URL}/api`;

const PAYMENT_COPY = {
  fr: {
    checkingTitle: "Vérification du paiement",
    checkingText: "Veuillez patienter pendant que nous confirmons votre paiement...",
    successTitle: "Paiement confirmé !",
    successText: "Votre paiement a été validé avec succès. Vous pouvez maintenant soumettre votre demande d'analyse.",
    submitOrder: "Soumettre ma commande",
    dashboard: "Aller au dashboard",
    errorTitle: "Erreur de paiement",
    errorText: "Nous n'avons pas pu confirmer votre paiement. Si vous avez été débité, veuillez nous contacter.",
    retry: "Réessayer",
    backDashboard: "Retour au dashboard",
  },
  en: {
    checkingTitle: "Checking payment",
    checkingText: "Please wait while we confirm your payment...",
    successTitle: "Payment confirmed!",
    successText: "Your payment was successfully validated. You can now submit your analysis request.",
    submitOrder: "Submit my order",
    dashboard: "Go to dashboard",
    errorTitle: "Payment error",
    errorText: "We could not confirm your payment. If you were charged, please contact us.",
    retry: "Try again",
    backDashboard: "Back to dashboard",
  },
};

const PaymentSuccessPage = () => {
  const { language } = useLanguage();
  const copy = PAYMENT_COPY[language] || PAYMENT_COPY.fr;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("checking"); // checking, paid, error
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 10;

  const pollPaymentStatus = useCallback(async () => {
    if (attempts >= maxAttempts) {
      setStatus("error");
      return;
    }

    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`, {
        withCredentials: true
      });

      if (data.payment_status === "paid") {
        setStatus("paid");
      } else if (data.status === "expired") {
        setStatus("error");
      } else {
        setAttempts((prev) => prev + 1);
      }
    } catch {
      setAttempts((prev) => prev + 1);
      if (attempts >= maxAttempts - 1) {
        setStatus("error");
      }
    }
  }, [attempts, sessionId]);

  useEffect(() => {
    if (!sessionId) {
      navigate("/dashboard");
      return;
    }

    if (status === "checking" && attempts < maxAttempts) {
      const timer = setTimeout(pollPaymentStatus, attempts === 0 ? 0 : 2000);
      return () => clearTimeout(timer);
    }
  }, [sessionId, navigate, status, attempts, pollPaymentStatus]);

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      <Header />

      <main className="min-h-screen flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          {status === "checking" && (
            <div className="bg-[#18181B] border border-white/10 p-8" data-testid="payment-checking">
              <Loader2 className="w-16 h-16 text-[#CCFF00] mx-auto mb-6 animate-spin" />
              <h1 className="text-2xl font-bold uppercase text-white mb-2">
                {copy.checkingTitle}
              </h1>
              <p className="text-zinc-500">
                {copy.checkingText}
              </p>
            </div>
          )}

          {status === "paid" && (
            <div className="bg-[#18181B] border border-white/10 p-8" data-testid="payment-success">
              <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <h1 className="text-2xl font-bold uppercase text-white mb-2">
                {copy.successTitle}
              </h1>
              <p className="text-zinc-500 mb-8">
                {copy.successText}
              </p>
              <div className="space-y-3">
                <Link
                  to="/order"
                  className="block w-full bg-[#CCFF00] text-black py-3 font-bold uppercase text-sm hover:bg-[#AACC00] transition-colors"
                  data-testid="goto-order"
                >
                  {copy.submitOrder}
                </Link>
                <Link
                  to="/dashboard"
                  className="block w-full border border-white/20 text-white py-3 font-bold uppercase text-sm hover:border-white hover:bg-white/5 transition-colors"
                >
                  {copy.dashboard}
                </Link>
              </div>
            </div>
          )}

          {status === "error" && (
            <div className="bg-[#18181B] border border-white/10 p-8" data-testid="payment-error">
              <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                <AlertCircle className="w-8 h-8 text-red-500" />
              </div>
              <h1 className="text-2xl font-bold uppercase text-white mb-2">
                {copy.errorTitle}
              </h1>
              <p className="text-zinc-500 mb-8">
                {copy.errorText}
              </p>
              <div className="space-y-3">
                <Link
                  to="/pricing"
                  className="block w-full bg-[#CCFF00] text-black py-3 font-bold uppercase text-sm hover:bg-[#AACC00] transition-colors"
                >
                  {copy.retry}
                </Link>
                <Link
                  to="/dashboard"
                  className="block w-full border border-white/20 text-white py-3 font-bold uppercase text-sm hover:border-white hover:bg-white/5 transition-colors"
                >
                  {copy.backDashboard}
                </Link>
              </div>
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default PaymentSuccessPage;
